import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  CForm,
  CInvalidFeedback,
  CFormGroup,
  CInput,
  CRow,
  CCol,
  CLabel,
  CButton,
} from '@coreui/react';
import { useForm, Controller } from 'react-hook-form';
import { isEmail } from '../../../../core/helpers/default';
import CustomSelect from '../../../../Shared/CustomSelect';
import TeamsAssign from '../TeamsAssign';
import CustomMultiSelect from '../../../../Shared/CustomMultiSelect';
import SyncSelect from '../../../../Shared/SyncSelect';
import useQueryParams from '../../../../core/hooks/useQueryParams';
import useCanPermissions from '../../../../core/hooks/useCanPermissions';
import permissions from '../../../../core/configs/permissions';
import { circulatorRoles } from '../../../../core/constants/roles';
import { circulatorLabel } from '../../../../core/constants/labels';
import _uniqBy from 'lodash/uniqBy';

const InviteForm = ({
  onSubmit,
  rolesOptions,
  isLoading,
  teamsOptions,
  labelsOptions,
  registrationFormsOptions,
  withEmail,
  submitText,
  showTeams,
  showLabels,
  showRoleSelect,
  showRegistrationForms,
}) => {
  const [organizations] = React.useState({});
  const [teams, setTeams] = React.useState({ assign: [] });
  const [labels, setLabels] = React.useState([]);
  const { getParam } = useQueryParams();
  const { isCan } = useCanPermissions();
  const assigneeEmail = decodeURIComponent(getParam('assignee_email') || '');
  const assigneeId = getParam('assignee_id');

  // Check if user can invite outside team
  const canInviteOutsideTeam = isCan({
    permission: permissions.can_invite_outside_team,
    initiativeMode: true,
    adminMode: false,
  });

  const { register, errors, handleSubmit, control } = useForm();

  const defaultRoleName = rolesOptions.length > 0 ? rolesOptions[0].id : '';

  // Pre-populate circulator label if default role is a circulator role
  useEffect(() => {
    if (defaultRoleName && circulatorRoles.includes(defaultRoleName) && labelsOptions.length > 0) {
      const circulatorLabelOption = labelsOptions.find((label) => label.name === circulatorLabel);
      setLabels([{ label: circulatorLabel, value: circulatorLabelOption?.id }]);
    }
  }, [defaultRoleName, labelsOptions]);

  /**
   * Handles the change event of the role select
   * Pre-select circulator label when a circulator role is selected
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} e event object
   * @returns void
   */
  const handleRoleChange = (e) => {
    const selectedRole = e.target.value;
    if (!selectedRole || !showLabels || labelsOptions.length === 0) return;

    const isCirculatorRole = circulatorRoles.includes(selectedRole);
    const circulatorLabelOption = labelsOptions.find((label) => label.name === circulatorLabel);

    // Remove circulator label if not a circulator role
    if (!isCirculatorRole && circulatorLabelOption) {
      setLabels((prevLabels) =>
        prevLabels.filter((label) => label.value !== circulatorLabelOption.id),
      );
    }

    // Add circulator label if a circulator role is selected
    if (isCirculatorRole && circulatorLabelOption) {
      setLabels((prevLabels) => {
        // Deduplicate applied labels by "value"
        return _uniqBy(
          [...prevLabels, { label: circulatorLabelOption.name, value: circulatorLabelOption.id }],
          'value',
        );
      });
    }
  };

  const onFormSubmit = (data) => {
    const dataKeys = Object.keys(data);
    const payload = {};
    dataKeys.forEach((k) => {
      if (data[k]) {
        payload[k] = data[k];
      }
    });

    if (!showRoleSelect) {
      payload.role = rolesOptions.length > 1 ? 'none' : rolesOptions[0].id;
    }

    if (data.registration_form === 'default') delete payload.registration_form;

    onSubmit({
      ...payload,
      teams,
      isAssignee: !!assigneeId,
      user_id: assigneeId ? Number(assigneeId) : undefined,
      labels: labels.map(({ value }) => value),
      organizations: organizations.assign,
    });
  };

  return (
    <CForm onSubmit={handleSubmit(onFormSubmit)}>
      <CRow>
        {showRoleSelect && (
          <CCol md="6" xs="12">
            <CFormGroup>
              <CustomSelect
                title="Role"
                placeholder="Select user role"
                id="role"
                name="role"
                options={rolesOptions.sort((a, b) => (b.name < a.name ? 1 : -1))}
                innerRef={register({
                  required: 'Role is required',
                })}
                invalid={!!errors.role}
                disabled={isLoading}
                className="mb-4"
                labelKey="name"
                valueKey="id"
                onChange={handleRoleChange}
              />
              <CInvalidFeedback>{!!errors.role && errors.role.message}</CInvalidFeedback>
            </CFormGroup>
          </CCol>
        )}
        {withEmail && (
          <CCol md="6" xs="12">
            <CFormGroup>
              <CLabel htmlFor="email">Email*</CLabel>
              <CInput
                type="text"
                name="email"
                defaultValue={assigneeEmail}
                innerRef={register({
                  required: 'Email is not valid',
                  validate: (val) => isEmail(val) || 'Email is not valid',
                })}
                onChange={(e) => {
                  e.target.value = e.target.value.trim();
                }}
                placeholder="Email"
                autoComplete="email"
                invalid={!!errors.email}
                disabled={isLoading}
              />
              <CInvalidFeedback>{!!errors.email && errors.email.message}</CInvalidFeedback>
            </CFormGroup>
          </CCol>
        )}
        {showTeams && (
          <CCol md="6" xs="12">
            <CFormGroup>
              <Controller
                control={control}
                name="teams"
                id="teams"
                rules={{
                  validate: (value) => {
                    if (
                      !canInviteOutsideTeam &&
                      (!value || !value.assign || value.assign.length === 0)
                    ) {
                      return 'Team is required';
                    }
                    return true;
                  },
                }}
                render={({ onChange, ref }) => (
                  <TeamsAssign
                    options={teamsOptions}
                    defaultValue={[]}
                    inputRef={ref}
                    onChange={(teamValue) => {
                      setTeams(teamValue);
                      onChange(teamValue);
                    }}
                  />
                )}
              />
              <CInvalidFeedback className="d-block">
                {!!errors.teams && errors.teams.message}
              </CInvalidFeedback>
            </CFormGroup>
          </CCol>
        )}
        {showLabels && (
          <CCol md="6" xs="12">
            <CFormGroup>
              <CustomMultiSelect
                title="Labels"
                type="text"
                onChange={setLabels}
                value={labels}
                options={labelsOptions.map((o) => ({ label: o.name, value: o.id }))}
              />
            </CFormGroup>
          </CCol>
        )}
        {showRegistrationForms && (
          <CCol md="6" xs="12">
            <CFormGroup className="mb-4">
              <Controller
                control={control}
                name="registration_form"
                id="registration_form"
                render={({ onChange, ref }) => (
                  <SyncSelect
                    label="Registration Form"
                    disabled={isLoading}
                    placeholder="Select"
                    defaultOptions={[
                      { id: 'default', name: 'Default Form' },
                      ...registrationFormsOptions,
                    ]}
                    inputRef={ref}
                    invalid={!!errors.registration_form}
                    defaultValue={{ id: 'default', name: 'Default Form' }}
                    getOptionValue={(option) => option.id}
                    getOptionLabel={(option) => option.name}
                    onChange={(option) => onChange(option.id)}
                  />
                )}
              />
              <CInvalidFeedback>
                {!!errors.registration_form && errors.registration_form.message}
              </CInvalidFeedback>
            </CFormGroup>
          </CCol>
        )}
      </CRow>
      <div className="text-right">
        <CButton type="submit" color="success">
          {submitText}
        </CButton>
      </div>
    </CForm>
  );
};

InviteForm.propTypes = {
  onSubmit: PropTypes.func,
  rolesOptions: PropTypes.arrayOf(PropTypes.shape()),
  organizationsOptions: PropTypes.arrayOf(PropTypes.shape()),
  teamsOptions: PropTypes.arrayOf(PropTypes.shape()),
  labelsOptions: PropTypes.arrayOf(PropTypes.shape()),
  registrationFormsOptions: PropTypes.arrayOf(PropTypes.shape()),
  isLoading: PropTypes.bool,
  withEmail: PropTypes.bool,
  showTeams: PropTypes.bool,
  showLabels: PropTypes.bool,
  showRoleSelect: PropTypes.bool,
  showRegistrationForms: PropTypes.bool,
  submitText: PropTypes.string,
};

InviteForm.defaultProps = {
  onSubmit: () => {},
  rolesOptions: [],
  organizationsOptions: [],
  teamsOptions: [],
  labelsOptions: [],
  registrationFormsOptions: [],
  isLoading: false,
  withEmail: false,
  showTeams: false,
  showLabels: true,
  showRoleSelect: false,
  showRegistrationForms: true,
  submitText: 'Invite',
};

export default InviteForm;
